import {
  Alert,
  Box,
  Button,
  IconButton,
  Snackbar,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { green, red } from "@mui/material/colors";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import React, { useEffect, useState } from "react";
import StartNotarizationModal from "../../modals/StartNotarizationModal";
import ReviewCreatedFormsModal from "../../modals/ReviewCreatedFormsModal";
import ContractorSignerTab from "./ContractorSignerTab";
import OwnerSignerTab from "./OwnerSignerTab";
import ScheduleContractorRonModal from "../../modals/ScheduleContractorRonModal";
import axios from "axios";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useSelector } from "react-redux";

const NotarizationTab = ({
  setDocNotaryTypeValue,
  docNotaryTypeValue,
  selectedPermitType,
}) => {
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const currentUser = useSelector((state) => state[currentSession]?.user);
  const { selectedRow } = useSelector((state) => state.myPermitsComponent);

  // Helper function to get data for the selected permit type
  const getPermitTypeData = () => {
    if (!selectedRow || !selectedPermitType) return selectedRow;

    // If it's the main permit type, return main data
    if (selectedPermitType === selectedRow.permit_type) {
      return selectedRow;
    }

    // If it's an additional permit, find it in additional_permits
    if (
      selectedRow.additional_permits &&
      selectedRow.additional_permits[selectedPermitType]
    ) {
      const additionalPermit =
        selectedRow.additional_permits[selectedPermitType];
      const permitData = Array.isArray(additionalPermit)
        ? additionalPermit[0]
        : additionalPermit;
      return {
        ...selectedRow, // Keep base properties like task_id, etc.
        ...permitData, // Override with permit-specific data
        // Ensure we use the additional permit's specific fields
        permit_package_created: permitData?.permit_package_created || false,
        noc_created: permitData?.noc_created || false,
        notarized: permitData?.notarized || false,
        noc_notarized: permitData?.noc_notarized || false,
        pp_notarized: permitData?.pp_notarized || false,
        selectedDocuments: permitData?.selected_documents || [],
        documents: permitData?.documents || {},
        processing_permit: permitData?.processing_permit || {},
      };
    }

    return selectedRow; // fallback to main data
  };

  const currentPermitData = getPermitTypeData();
  const row = currentPermitData;
  const { processing_permit } = row;

  // Helper function to check if there are pending additional contractors
  const hasPendingAdditionalContractors = () => {
    const additionalContractors =
      processing_permit?.notarization?.additional_contractors || [];
    return additionalContractors.some(
      (contractor) =>
        contractor?.notarization?.is_notarized !== true &&
        contractor?.notarization?.is_signed !== true
    );
  };

  // Get the current user's contractor info from additional_contractors (if they're one)
  const getCurrentUserAdditionalContractor = () => {
    // Get the current logged-in user's email (handles main user and subuser)
    const userEmail = (
      currentUser?.subuser?.email ||
      currentUser?.user?.email ||
      currentUser?.email ||
      ""
    ).toLowerCase();

    if (!userEmail) return null;

    const additionalContractors =
      processing_permit?.notarization?.additional_contractors || [];

    const found = additionalContractors.find(
      (contractor) => contractor?.email?.toLowerCase() === userEmail
    );

    return found;
  };

  const [notarizeModalOpen, setNotarizeModalOpen] = useState(false);
  const [reviewDocModalOpen, setReviewDocModalOpen] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [signerValue, setSignerValue] = useState(0);
  const [isFading, setIsFading] = useState(false);
  const [ronScheduleOpen, setRonScheduleOpen] = useState(false);
  const [ronStarted, setRonStarted] = useState(false);
  const [fiveMinBeforeSession, setFiveMinBeforeSession] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isOwnerSign, setIsOwnerSign] = useState(false);

  const lg = useMediaQuery((theme) => theme.breakpoints.up("lg"));

  const snackHandleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackOpen(false);
  };

  useEffect(() => {
    const docType =
      currentPermitData?.processing_permit?.notarization?.notarizing_docs;

    if ([0, 1, 2].includes(docType)) {
      const ownerHasDocs = currentPermitData?.selectedDocuments?.some((doc) => {
        if (docType === 0) {
          return true; // Always true if docType is 0
        }

        if (docType >= 1 && doc.name !== "noc") {
          return (
            doc?.signatures?.owner?.sign === true ||
            doc?.signatures?.owner?.notary === true
          ); // True if owner has signed or notarized this document
        }

        return false; // False if no conditions are met
      });
      if (isOwnerSign !== ownerHasDocs) {
        setIsOwnerSign(ownerHasDocs);
        if (!ownerHasDocs && signerValue === 1) {
          setSignerValue(0);
        }
      }
    }
  }, [
    isOwnerSign,
    signerValue,
    currentPermitData?.processing_permit?.notarization?.notarizing_docs,
    currentPermitData?.selectedDocuments,
  ]);

  useEffect(() => {
    if (
      currentPermitData?.noc_created &&
      !currentPermitData?.permit_package_created
    ) {
      setDocNotaryTypeValue(1);
    } else if (currentPermitData?.permit_package_created) {
      setDocNotaryTypeValue(0);
    } else if (
      currentPermitData?.documents?.pp_ntbo?.is_created &&
      !currentPermitData?.noc_created
    ) {
      // pp_ntbo only (without NOC) - set to "All Documents" (which will just be pp_ntbo)
      setDocNotaryTypeValue(0);
    }
  }, [currentPermitData, setDocNotaryTypeValue]);

  const handleSignerChange = (event, newValue) => {
    setIsFading(true);
    setTimeout(() => {
      setSignerValue(newValue);
      setIsFading(false);
    }, 300); // Duration should match the CSS transition duration
  };

  const handleDocNotaryTypeChange = (event, newValue) => {
    setIsFading(true);
    setTimeout(() => {
      setDocNotaryTypeValue(newValue);
      setIsFading(false);
    }, 300); // Duration should match the CSS transition duration
  };

  useEffect(() => {
    const contractor =
      currentPermitData?.processing_permit?.notarization?.contractor;
    const owner = currentPermitData?.processing_permit?.notarization?.owner;
    const signingOrder =
      currentPermitData?.processing_permit?.notarization?.signing_order;
    const contractorSigned = contractor?.notarization?.is_signed === true;
    const contractorNotarized = contractor?.notarization?.is_notarized === true;
    const ownerSigned = owner?.notarization?.is_signed === true;
    const ownerNotarized = owner?.notarization?.is_notarized === true;
    const ownerOnlySign = owner?.notarization?.only_sign === true;
    const ownerCompletedESign = ownerOnlySign && ownerSigned;
    const ownerNeedsToComplete =
      owner &&
      (owner?.notarization?.is_active === true ||
        owner?.notarization?.waiting_to_schedule === true) &&
      !ownerSigned &&
      !ownerNotarized;

    if (
      signingOrder === "contractor" &&
      (contractorSigned || contractorNotarized)
    ) {
      // If contractor has completed and owner still needs to complete, show owner tab
      if (
        ownerNeedsToComplete &&
        (isOwnerSign || currentPermitData?.selectedDocuments?.length > 1)
      ) {
        setSignerValue(1);
      } else if (
        currentPermitData?.selectedDocuments?.length === 1 ||
        !isOwnerSign
      ) {
        setSignerValue(0);
      } else {
        setSignerValue(1);
      }
    } else if (signingOrder === "owner" && (ownerNotarized || ownerCompletedESign)) {
      // If owner has completed (either notarized or e-sign), show contractor tab if contractor exists
      if (
        contractor &&
        Object.keys(contractor).length > 0 &&
        !contractorSigned &&
        !contractorNotarized
      ) {
        setSignerValue(1);
      } else if (currentPermitData?.selectedDocuments?.length === 1) {
        setSignerValue(0);
      } else {
        setSignerValue(1);
      }
    } else {
      setSignerValue(0);
    }
  }, [
    isOwnerSign,
    currentPermitData?.selectedDocuments?.length,
    currentPermitData?.processing_permit?.notarization?.contractor,
    currentPermitData?.processing_permit?.notarization?.owner,
    currentPermitData?.processing_permit?.notarization?.signing_order,
  ]);

  useEffect(() => {
    if (ronStarted) {
      const timeOut = setTimeout(() => {
        setRonStarted(false);
      }, 15000);
      return () => clearTimeout(timeOut);
    }
  }, [ronStarted]);

  const getNotaryTypeTabComponent = () => {
    switch (docNotaryTypeValue) {
      case 0:
      case 1:
      case 2:
        return (
          <>
            <Typography
              align="center"
              variant="h5"
              mt={1}
              sx={{ color: "#388e3c" }}
            >
              Notarize/sign{" "}
            </Typography>
            <Typography
              align="center"
              variant="h5"
              mt={1}
              sx={{ color: "#388e3c" }}
            >
              {docNotaryTypeValue === 0
                ? "All Documents"
                : docNotaryTypeValue === 1
                  ? "NOC Form"
                  : "Permit Forms"}
            </Typography>
            {docNotaryTypeValue === 1 &&
              selectedRow?.documents?.noc_affidavit?.is_created === true && (
                <Typography
                  px={2}
                  align="center"
                  variant="body2"
                  fontWeight="bold"
                  color="primary"
                >
                  (Affidavit Included)
                </Typography>
              )}
            <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
              <Button
                disabled={
                  selectedRow?.city_scraper?.is_accepted &&
                  !selectedRow?.city_scraper?.first_done
                }
                onClick={() => {
                  setNotarizeModalOpen(true);
                }}
                variant="contained"
              >
                Get Started
              </Button>
            </Box>
          </>
        );

      default:
        return <></>;
    }
  };

  const getSignerTabComponent = () => {
    switch (signerValue) {
      case 0:
        return processing_permit?.notarization?.signing_order ===
          "contractor" ? (
          <ContractorSignerTab
            selectedRow={currentPermitData}
            fiveMinBeforeSession={fiveMinBeforeSession}
            setFiveMinBeforeSession={setFiveMinBeforeSession}
            setRonScheduleOpen={setRonScheduleOpen}
            isOwnerSign={isOwnerSign}
          />
        ) : (
          <OwnerSignerTab selectedRow={currentPermitData} />
        );
      case 1:
        return processing_permit?.notarization?.signing_order ===
          "contractor" ? (
          <OwnerSignerTab selectedRow={currentPermitData} />
        ) : (
          <ContractorSignerTab
            selectedRow={currentPermitData}
            fiveMinBeforeSession={fiveMinBeforeSession}
            setFiveMinBeforeSession={setFiveMinBeforeSession}
            setRonScheduleOpen={setRonScheduleOpen}
            isOwnerSign={isOwnerSign}
          />
        );

      default:
        return (
          <ContractorSignerTab
            selectedRow={currentPermitData}
            fiveMinBeforeSession={fiveMinBeforeSession}
            setFiveMinBeforeSession={setFiveMinBeforeSession}
            setRonScheduleOpen={setRonScheduleOpen}
            isOwnerSign={isOwnerSign}
          />
        );
    }
  };

  const handleRefreshContractorSignOnly = async () => {
    try {
      setLoading(true);
      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/notary/refresh-contractor-sign`,
        {
          task_id: selectedRow?.task_id,
          currentSession: currentSession,
        },
        {
          withCredentials: true,
        }
      );
      if (resp.data.success === true) {
      }
    } catch (e) {
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshOwnerSignOnly = async () => {
    try {
      setLoading(true);
      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/notary/refresh-owner-sign`,
        {
          task_id: selectedRow?.task_id,
          currentSession: currentSession,
        },
        {
          withCredentials: true,
        }
      );
      if (resp.data.success === true) {
        // Refresh successful, component will re-render with updated data
      }
    } catch (e) {
      // Handle error silently or show notification
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          position: "relative",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          mt: 1,
        }}
      >
        <img
          alt="notarization"
          src="/img/website_img/notarize.png"
          style={{
            width: "100%",
            maxWidth: "4rem",
            height: "auto",
            borderRadius: "20px",
          }}
        />

        {currentPermitData?.processing_permit?.notarization?.contractor
          ?.notarization?.only_sign &&
          !currentPermitData?.processing_permit?.notarization?.contractor
            ?.notarization?.is_signed &&
          processing_permit?.notarization?.signing_order === "contractor" &&
          signerValue === 0 && (
            <Box sx={{ position: "absolute", top: 0, right: 0 }}>
              <Tooltip title="Refresh">
                <IconButton
                  onClick={() => {
                    if (loading) {
                      return;
                    } else {
                      handleRefreshContractorSignOnly();
                    }
                  }}
                >
                  <RefreshIcon className={loading ? "rotate" : undefined} />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        {currentPermitData?.processing_permit?.notarization?.owner?.notarization
          ?.only_sign &&
          !currentPermitData?.processing_permit?.notarization?.owner
            ?.notarization?.is_signed &&
          ((processing_permit?.notarization?.signing_order === "owner" &&
            signerValue === 0) ||
            (processing_permit?.notarization?.signing_order === "contractor" &&
              currentPermitData?.processing_permit?.notarization?.contractor
                ?.notarization?.is_signed &&
              signerValue === 1)) && (
            <Box sx={{ position: "absolute", top: 0, right: 0 }}>
              <Tooltip title="Refresh">
                <IconButton
                  onClick={() => {
                    if (loading) {
                      return;
                    } else {
                      handleRefreshOwnerSignOnly();
                    }
                  }}
                >
                  <RefreshIcon className={loading ? "rotate" : undefined} />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        {!currentPermitData?.permit_package_created &&
        (!currentPermitData?.noc_created ||
          currentPermitData?.documents?.noc_affidavit?.is_created === false) &&
        !currentPermitData?.documents?.pp_ntbo?.is_created ? (
          //? DEFAULT DISPLAY
          <Box mt={6}>
            <Typography align="center" px={1} variant="h6" color="primary">
              Notarization requests have not yet been initiated
            </Typography>
          </Box>
        ) : //? START NOTARIZATION
        !currentPermitData?.notarized &&
          !processing_permit?.notarization?.started ? (
          <>
            <Typography
              align="center"
              px={1}
              variant="h6"
              sx={{ pt: 1 }}
              color="primary"
            >
              Select the type of document(s) you would like to notarize
            </Typography>
            <Box
              sx={{
                maxWidth: "100%",
                bgcolor: "background.paper",
              }}
            >
              <Tabs
                value={docNotaryTypeValue}
                onChange={handleDocNotaryTypeChange}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                aria-label="scrollable auto tabs example"
                TabIndicatorProps={{
                  style: {
                    height: "3px", // Customize the height of the indicator
                    bottom: "10px", // Adjust the position closer to the text
                  },
                }}
              >
                <Tab
                  disabled={
                    currentPermitData?.noc_created &&
                    !currentPermitData?.permit_package_created
                  }
                  label="All Documents"
                  disableRipple
                />
                <Tab
                  disabled={!currentPermitData?.noc_created}
                  label="NOC Only"
                  disableRipple
                />
                {/* <Tab
                  disabled={!selectedRow?.permit_package_created}
                  label="Permit Forms"
                  disableRipple
                /> */}
              </Tabs>
            </Box>
            <Box
              sx={{ width: "100%" }}
              className={
                isFading
                  ? "fade-exit fade-exit-active"
                  : "fade-enter fade-enter-active"
              }
            >
              {getNotaryTypeTabComponent()}
            </Box>
          </>
        ) : !currentPermitData?.notarized &&
          !processing_permit?.notarization?.finalized &&
          processing_permit?.notarization?.started ? (
          <>
            <Box
              sx={{
                maxWidth: "100%",
                bgcolor: "background.paper",
              }}
            >
              <Tabs
                value={signerValue}
                onChange={handleSignerChange}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                aria-label="scrollable auto tabs example"
                sx={{
                  "& .MuiTab-root": {
                    minWidth: "auto",

                    padding: "12px 0px",
                    marginRight: "0px",
                  },
                }}
                TabIndicatorProps={{
                  style: {
                    height: "3px", // Customize the height of the indicator
                    bottom: "10px", // Adjust the position closer to the text
                  },
                }}
              >
                <Tab
                  sx={{
                    ml:
                      Object.keys(processing_permit?.notarization?.contractor)
                        .length === 0 ||
                      (!isOwnerSign &&
                        !selectedRow?.documents?.noc_affidavit?.is_created)
                        ? 11
                        : undefined,
                  }}
                  label={
                    processing_permit?.notarization?.signing_order ===
                    "contractor"
                      ? "1. Contractor"
                      : "1. Owner/Agent"
                  }
                  disableRipple
                />


                <Tab
                  sx={{
                    visibility: (() => {
                      const contractor =
                        processing_permit?.notarization?.contractor;
                      const owner = processing_permit?.notarization?.owner;
                      const contractorExists =
                        contractor && Object.keys(contractor).length > 0;
                      const ownerNeedsToComplete =
                        owner &&
                        (owner?.notarization?.is_active === true ||
                          owner?.notarization?.waiting_to_schedule === true) &&
                        !owner?.notarization?.is_signed &&
                        !owner?.notarization?.is_notarized;
                      const ownerOnlySign = owner?.notarization?.only_sign === true;
                      const ownerCompletedESign = ownerOnlySign && owner?.notarization?.is_signed === true;

                      // Show if contractor exists AND (isOwnerSign OR noc_affidavit exists OR owner needs to complete OR owner completed e-sign)
                      if (
                        contractorExists &&
                        (isOwnerSign ||
                          selectedRow?.documents?.noc_affidavit?.is_created ||
                          ownerNeedsToComplete ||
                          ownerCompletedESign)
                      ) {
                        return undefined;
                      }
                      // Hide otherwise
                      return "hidden";
                    })(),
                    ml: 2, // Add left margin to create gap between tabs
                  }}
                  label={
                    processing_permit?.notarization?.signing_order ===
                    "contractor"
                      ? "2. Owner/Agent"
                      : "2. Contractor"
                  }
                  disableRipple
                />
              </Tabs>
            </Box>
            {/* Show IPEN/RON Session title below tabs */}
            {signerValue === 0 &&
              processing_permit?.notarization?.signing_order === "owner" && (
                <Typography
                  variant="caption"
                  align="center"
                  sx={{
                    fontSize: "0.75rem",
                    color: "text.secondary",
                    mt: 0.5,
                    mb: 1,
                  }}
                >
                  {processing_permit?.notarization?.owner?.notarization?.only_sign
                    ? "E-Sign Session"
                    : processing_permit?.notarization?.owner?.notarization?.is_ipen
                      ? "IPEN Session"
                      : "RON Session"}
                </Typography>
              )}
            {signerValue === 1 &&
              processing_permit?.notarization?.signing_order ===
                "contractor" && (
                <Typography
                  variant="caption"
                  align="center"
                  sx={{
                    fontSize: "0.75rem",
                    color: "text.secondary",
                    mt: 0.5,
                    mb: 1,
                  }}
                >
                  {processing_permit?.notarization?.owner?.notarization?.only_sign
                    ? "E-Sign Session"
                    : processing_permit?.notarization?.owner?.notarization?.is_ipen
                      ? "IPEN Session"
                      : "RON Session"}
                </Typography>
              )}
            <Box
              sx={{ width: "100%" }}
              className={
                isFading
                  ? "fade-exit fade-exit-active"
                  : "fade-enter fade-enter-active"
              }
            >
              {getSignerTabComponent()}
            </Box>
          </>
        ) : currentPermitData?.notarized &&
          !hasPendingAdditionalContractors() ? (
          <>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography align="center" variant="h5" color="primary" mt={2}>
                Notarized Permit Package
              </Typography>
              <CheckCircleIcon
                style={{
                  color: green[500],
                  marginLeft: 8,
                  marginTop: 15,
                  fontSize: "30px",
                }}
              />
            </Box>

            <Typography
              variant="body2"
              fontWeight="bold"
              color="primary"
              align="center"
              px={2}
              mt={2}
            >
              All of your documents have been notarized/signed successfully. The
              notarized/signed documents can be downloaded by clicking on the
              download button for each file.
            </Typography>
          </>
        ) : hasPendingAdditionalContractors() ? (
          <>
            {/* Check if current user is one of the additional contractors */}
            {getCurrentUserAdditionalContractor() ? (
              // Current user IS an additional contractor - show their action UI
              (() => {
                const myContractorData = getCurrentUserAdditionalContractor();
                const isIpen = myContractorData?.notarization?.is_ipen === true;
                const ipenPin = myContractorData?.notarization?.ipen_pin;
                const waitingToSchedule =
                  myContractorData?.notarization?.waiting_to_schedule === true;
                const isDone =
                  myContractorData?.notarization?.is_notarized === true ||
                  myContractorData?.notarization?.is_signed === true;

                if (isDone) {
                  return (
                    <Box sx={{ textAlign: "center", mt: 2 }}>
                      <Typography variant="h5" color="primary">
                        Your Notarization Complete
                      </Typography>
                      <CheckCircleIcon
                        style={{
                          color: green[500],
                          fontSize: "40px",
                          marginTop: 8,
                        }}
                      />
                      <Typography variant="body2" color="text.secondary" mt={2}>
                        Waiting for other contractors to complete their
                        sessions.
                      </Typography>
                    </Box>
                  );
                }

                if (isIpen && ipenPin) {
                  return (
                    <Box sx={{ textAlign: "center", mt: 2 }}>
                      <Typography variant="h5" color="primary">
                        Your IPEN Session
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mt={1}>
                        Provide this PIN to your notary to start the session:
                      </Typography>
                      <Typography
                        variant="h3"
                        color="primary"
                        fontWeight="bold"
                        mt={2}
                        sx={{
                          letterSpacing: "0.3em",
                          backgroundColor: "#e3f2fd",
                          borderRadius: "8px",
                          py: 2,
                          mx: 4,
                        }}
                      >
                        {ipenPin}
                      </Typography>
                    </Box>
                  );
                }

                if (waitingToSchedule) {
                  return (
                    <Box sx={{ textAlign: "center", mt: 2 }}>
                      <Typography variant="h5" color="primary">
                        Schedule Your RON Session
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mt={1}>
                        Click below to schedule your remote online notarization
                        session.
                      </Typography>
                      <Button
                        variant="contained"
                        color="primary"
                        sx={{ mt: 3 }}
                        onClick={() => setRonScheduleOpen(true)}
                      >
                        Schedule RON Session
                      </Button>
                    </Box>
                  );
                }

                return null;
              })()
            ) : (
              // Current user is NOT an additional contractor - show status overview
              <>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    align="center"
                    variant="h5"
                    color="primary"
                    mt={2}
                  >
                    Notarization In Progress
                  </Typography>
                  <CancelIcon
                    style={{
                      color: red[500],
                      marginLeft: 8,
                      marginTop: 15,
                      fontSize: "30px",
                    }}
                  />
                </Box>

                <Typography
                  variant="body2"
                  fontWeight="bold"
                  color="primary"
                  align="center"
                  px={2}
                  mt={2}
                >
                  Waiting for additional contractors to complete their
                  notarization/signing sessions.
                </Typography>

                {/* Show additional contractors status */}
                <Box sx={{ mt: 3, px: 2 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    color="primary"
                    align="center"
                    mb={2}
                  >
                    Additional Contractors Status:
                  </Typography>
                  {(
                    processing_permit?.notarization?.additional_contractors ||
                    []
                  ).map((contractor, index) => {
                    const isDone =
                      contractor?.notarization?.is_notarized === true ||
                      contractor?.notarization?.is_signed === true;
                    const isIpen = contractor?.notarization?.is_ipen === true;
                    const ipenPin = contractor?.notarization?.ipen_pin;
                    const waitingToSchedule =
                      contractor?.notarization?.waiting_to_schedule === true;

                    return (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          p: 1.5,
                          mb: 1,
                          border: isDone
                            ? "1px solid #10b981"
                            : "1px solid #f59e0b",
                          borderRadius: "8px",
                          backgroundColor: isDone ? "#f0fdf4" : "#fffbeb",
                        }}
                      >
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {contractor?.business_name || "Contractor"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {contractor?.email}
                          </Typography>
                          {!isDone && isIpen && ipenPin && (
                            <Typography
                              variant="caption"
                              display="block"
                              color="primary"
                              fontWeight="bold"
                            >
                              IPEN PIN: {ipenPin}
                            </Typography>
                          )}
                          {!isDone && !isIpen && waitingToSchedule && (
                            <Typography
                              variant="caption"
                              display="block"
                              color="warning.main"
                            >
                              Waiting to schedule RON session
                            </Typography>
                          )}
                        </Box>
                        {isDone ? (
                          <CheckCircleIcon style={{ color: green[500] }} />
                        ) : (
                          <CancelIcon style={{ color: "#f59e0b" }} />
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </>
            )}
          </>
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography align="center" variant="h5" color="primary" mt={2}>
                Notarization Overview
              </Typography>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", mt: 2 }}>
              {[
                { name: "noc", title: "Notice of Commencement (NOC)" },
                { name: "pp", title: "Permit Forms" },
              ].map((item, index) => {
                const notarized =
                  currentPermitData?.notarized ||
                  (item.name === "noc" &&
                    currentPermitData?.noc_notarized === true) ||
                  (item.name === "pp" &&
                    currentPermitData?.pp_notarized === true)
                    ? true
                    : false;
                return (
                  <Box key={index} mb={3}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography
                        variant="body1"
                        fontWeight="bold"
                        color="primary"
                        align="center"
                      >
                        {item.title}
                      </Typography>
                      {notarized ? (
                        <CheckCircleIcon
                          style={{
                            color: green[500],
                            marginLeft: 8,

                            fontSize: "25px",
                          }}
                        />
                      ) : (
                        <CancelIcon
                          style={{
                            color: red[500],
                            marginLeft: 8,

                            fontSize: "25px",
                          }}
                        />
                      )}
                    </Box>
                    <Box
                      sx={{
                        mt: 0.5,
                        display: "flex",
                        justifyContent: "center",
                        border: notarized
                          ? "1px solid #10b981"
                          : "1px solid #f44336",
                        borderRadius: "5px",
                        py: 0.3,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ color: notarized ? "#10b981" : "#f44336" }}
                        align="center"
                        fontWeight="bold"
                        px={1}
                      >
                        {notarized ? "NOTARIZED" : "NOT SELECTED"}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </>
        )}
      </Box>
      {notarizeModalOpen && (
        <StartNotarizationModal
          open={notarizeModalOpen}
          setOpen={setNotarizeModalOpen}
          setReviewDocModalOpen={setReviewDocModalOpen}
          setSnackOpen={setSnackOpen}
          setRonScheduleOpen={setRonScheduleOpen}
          docNotaryTypeValue={docNotaryTypeValue}
          // setAddPaymentModalOpen={setAddPaymentModalOpen}
        />
      )}

      {reviewDocModalOpen && (
        <ReviewCreatedFormsModal
          open={reviewDocModalOpen}
          setOpen={setReviewDocModalOpen}
          docNotaryTypeValue={docNotaryTypeValue}
        />
      )}


      {ronScheduleOpen && (
        <ScheduleContractorRonModal
          open={ronScheduleOpen}
          setOpen={setRonScheduleOpen}
          selectedRow={currentPermitData}
          setRonStarted={setRonStarted}
          setSnackOpen={setSnackOpen}
          ownerData={selectedRow?.property_owner}
          ownerZone={
            selectedRow?.property_owner?.timzeZone ||
            selectedRow?.zone ||
            "America/New_York"
          }
          fiveMinBeforeSession={fiveMinBeforeSession}
        />
      )}

      {/* <AddPaymentCardModal
        addPaymentModalOpen={addPaymentModalOpen}
        setAddPaymentModalOpen={setAddPaymentModalOpen}
        stripe={stripe}
        elements={elements}
        CardElement={CardElement}
        addCardType={addCardType}
        setAddCardType={setAddCardType}
      /> */}
      <Snackbar
        sx={{ zIndex: 9999999 }}
        open={snackOpen}
        autoHideDuration={4000}
        onClose={snackHandleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={snackHandleClose}
          severity="success" // Changed severity to success
          sx={{
            width: "100%",
            marginTop: "80px",
            marginLeft: lg ? "280px" : "0px",
            backgroundColor: "#388e3c", // Green shade for success
            color: "#fff",
            fontWeight: "bold",
          }}
          iconMapping={{
            success: <CheckCircleIcon sx={{ color: "#fff" }} />, // Changed to success icon
          }}
        >
          {ronStarted
            ? "RON Session has been scheduled successfully!"
            : "Payment Successful. Thank you"}
        </Alert>
      </Snackbar>
    </>
  );
};

export default React.memo(NotarizationTab);
